/**
 * Navigation helpers for RegimenDayModal (Bug #14B fix)
 * 
 * These pure functions are extracted from the component to enable unit testing.
 * They allow navigation to workouts regardless of how far apart they are,
 * replacing the old ±14 day window limitation.
 */

import { addDays } from 'date-fns';

/**
 * Build a sorted list of all workout dates from regimens.
 * 
 * @param {Array} regimens - Array of regimen objects with start_of_week_date and regimen_titles
 * @returns {Date[]} - Sorted array of Date objects (ascending by time)
 */
export function buildWorkoutDateList(regimens) {
  const dates = [];
  
  if (!regimens || !Array.isArray(regimens)) {
    return dates;
  }
  
  regimens.forEach(regimen => {
    if (!regimen.start_of_week_date || !regimen.regimen_titles) return;
    
    const weekStart = new Date(regimen.start_of_week_date + 'T00:00:00');
    
    regimen.regimen_titles.forEach((title, dayIndex) => {
      // Only include days where there's actually a workout (title is truthy)
      if (title) {
        const workoutDate = addDays(weekStart, dayIndex);
        dates.push(workoutDate);
      }
    });
  });
  
  // Sort ascending by time
  dates.sort((a, b) => a.getTime() - b.getTime());
  
  return dates;
}

/**
 * Find the next workout date after the current date.
 * 
 * @param {Date[]} allDates - Sorted array of workout dates
 * @param {Date} currentDate - The current date to search from
 * @returns {Date|null} - The next workout date, or null if none exists
 */
export function findNextWorkoutDate(allDates, currentDate) {
  if (!allDates || !currentDate) return null;
  
  const currentTime = currentDate.getTime();
  
  // Find first workout date after currentDate
  return allDates.find(d => d.getTime() > currentTime) || null;
}

/**
 * Find the previous workout date before the current date.
 * 
 * @param {Date[]} allDates - Sorted array of workout dates
 * @param {Date} currentDate - The current date to search from
 * @returns {Date|null} - The previous workout date, or null if none exists
 */
export function findPrevWorkoutDate(allDates, currentDate) {
  if (!allDates || !currentDate) return null;
  
  const currentTime = currentDate.getTime();
  
  // Find last workout date before currentDate (iterate from end)
  for (let i = allDates.length - 1; i >= 0; i--) {
    if (allDates[i].getTime() < currentTime) {
      return allDates[i];
    }
  }
  
  return null;
}



